import { useState } from "react";
import { AlertCircle, X } from "lucide-react";

import { INTERACTION_TYPES } from "../consts";
import { callApi, getCourseLabel, getGroupRequestBody } from "../utils";
import EditPracticalsCount from "./edit-practicals-count";
import EditPracticals from "./edit-practicals";
import EditSubgroups from "./edit-subgroups";
import EditVariantsAssignment from "./edit-variants-assignment";

const getNewPracticals = (practicals) =>
  practicals.length
    ? practicals
    : [
        {
          number: 1,
          variantsCount: 1,
          startDate: "",
          endDate: "",
        },
      ];

export default function EditGroupModal({
  group,
  practicals,
  subgroups,
  subgroupVariants,
  studentVariants,
  onCancel,
  onSaved,
}) {
  const [newPracticals, setNewPracticals] = useState(
    getNewPracticals(practicals),
  );
  const [practicalsCount, setPracticalsCount] = useState(
    practicals.length || 1,
  );
  const [subgroupsCount, setSubgroupsCount] = useState(
    group.subgroupsCount || 1,
  );
  const [newSubgroups, setNewSubgroups] = useState(subgroups);
  const [newSubgroupVariants, setNewSubgroupVariants] =
    useState(subgroupVariants);
  const [newStudentVariants, setNewStudentVariants] = useState(studentVariants);
  const [errors, setErrors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const isGroup = group.interactionType === INTERACTION_TYPES.group;

  const getPayload = (confirmMaterialsDeletion) =>
    getGroupRequestBody({
      practicals: newPracticals,
      subgroupVariants,
      confirmMaterialsDeletion,
      ...(isGroup
        ? {
            subgroupsCount,
            students: newSubgroups,
            subgroupVariants: newSubgroupVariants,
          }
        : { studentVariants: newStudentVariants }),
    });

  const validate = () => {
    const nextErrors = [];

    newPracticals.forEach((item) => {
      if (!item.startDate || !item.endDate) {
        nextErrors.push(
          `Заполните период выполнения для практической работы № ${item.number}.`,
        );
      } else if (item.startDate > item.endDate) {
        nextErrors.push(
          `Дата завершения практической работы № ${item.number} раньше даты начала.`,
        );
      }
    });
    return nextErrors;
  };

  const requestPreview = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (nextErrors.length) return;

    const response = await callApi({
      method: "POST",
      path: `api/teacher/practice-groups/${group.id}/edit-preview`,
      body: getPayload(),
      needAuth: true,
    });
    if (response.error) {
      setErrors(
        response.details?.length
          ? response.details
          : [response.message || "Не удалось проверить изменения."],
      );
      return;
    }
    setPreview(response.response || { changes: [], warnings: [] });
  };

  const save = async () => {
    setSaving(true);
    const response = await callApi({
      method: "PUT",
      path: `api/teacher/practice-groups/${group.id}`,
      body: getPayload(true),
      needAuth: true,
    });
    setSaving(false);
    if (response.error) {
      setErrors(
        response.details?.length
          ? response.details
          : [response.message || "Не удалось сохранить изменения."],
      );
      setPreview(null);
    } else {
      onSaved();
    }
  };

  const renderParameters = () => (
    <>
      <div className="edit-group-header">
        <h4>{group.group}</h4>
        <p className="muted">
          {`${getCourseLabel(group.course)} · ${isGroup ? "Групповой формат" : "Индивидуальный формат"}`}
        </p>
      </div>

      {isGroup && (
        <EditSubgroups
          subgroupsCount={subgroupsCount}
          setSubgroupsCount={setSubgroupsCount}
          subgroups={newSubgroups}
          setSubgroups={setNewSubgroups}
        />
      )}

      <EditPracticalsCount
        practicalsCount={practicalsCount}
        setPracticalsCount={setPracticalsCount}
        setPracticals={setNewPracticals}
      />

      <EditPracticals
        practicals={newPracticals}
        setPracticals={setNewPracticals}
      />

      <EditVariantsAssignment
        isGroup={isGroup}
        practicals={newPracticals}
        subgroupsCount={subgroupsCount}
        studentVariants={newStudentVariants}
        setStudentVariants={setNewStudentVariants}
        subgroupVariants={newSubgroupVariants}
        setSubgroupVariants={setNewSubgroupVariants}
      />
    </>
  );

  const renderChanges = () => (
    <>
      <h4>Изменения</h4>
      {preview.changes?.length ? (
        preview.changes.map((change, index) => (
          <div className="change-row" key={index}>
            {change}
          </div>
        ))
      ) : (
        <div className="muted">Изменений нет.</div>
      )}
      {preview.warnings?.length > 0 && (
        <div className="warning-box">
          {preview.warnings.map((warning, index) => (
            <div key={index}>
              <AlertCircle size={14} /> {warning}
            </div>
          ))}
        </div>
      )}
      <p className="muted small">
        После подтверждения изменения вступят в силу сразу. Материалы удаляемых
        практических работ также будут удалены.
      </p>
    </>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal edit-group">
        <div className="modal-head">
          <h2>
            {preview ? "Подтверждение изменений" : "Редактировать группу"}
          </h2>
          <button className="icon-btn" onClick={onCancel} aria-label="Закрыть">
            <X size={18} />
          </button>
        </div>
        <div className="modal-body">
          {preview ? renderChanges() : renderParameters()}
          {errors.length > 0 && (
            <div className="error-box">
              {errors.map((error, index) => (
                <div key={index}>
                  <AlertCircle size={14} /> {error}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button
            className="btn ghost"
            onClick={preview ? () => setPreview(null) : onCancel}
          >
            Отмена
          </button>
          {preview ? (
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
          ) : (
            <button className="btn primary" onClick={requestPreview}>
              Проверить изменения
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
